import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Link2, Copy, Check, Trash2, Save, Shield, ShieldOff, ExternalLink, AlertTriangle } from "lucide-react";

const BRIDGE_BADGE = {
  active: { label: "Active", cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" },
  token_expired: { label: "Token expired", cls: "bg-rose-500/10 text-rose-300 border-rose-500/30" },
  token_missing: { label: "Token invalid", cls: "bg-rose-500/10 text-rose-300 border-rose-500/30" },
  token_invalid: { label: "Token invalid", cls: "bg-rose-500/10 text-rose-300 border-rose-500/30" },
  reauth_needed: { label: "Reauth needed", cls: "bg-amber-500/10 text-amber-300 border-amber-500/30" },
  provider_id_missing: { label: "Provider not configured", cls: "bg-amber-500/10 text-amber-300 border-amber-500/30" },
  not_installed: { label: "Not installed", cls: "bg-zinc-800/40 text-zinc-400 border-zinc-700" },
};

export default function AiSensyUserPanel() {
  const [conn, setConn] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [secretDraft, setSecretDraft] = useState("");
  const [savingSecret, setSavingSecret] = useState(false);
  const [bridge, setBridge] = useState(null);
  const [installing, setInstalling] = useState(false);
  const [lastAttempt, setLastAttempt] = useState(null);

  const load = () => {
    api.get("/aisensy/connection").then((r) => {
      setConn(r.data);
      setSecretDraft(r.data?.webhook_secret || "");
    }).catch(() => setConn({ status: "not_connected" }));
    api.get("/employz-app/install-status").then((r) => setBridge(r.data)).catch(() => setBridge({ status: "not_installed", bridge_state: "not_installed" }));
    api.get("/employz-app/last-install-attempt").then((r) => setLastAttempt(r.data?.found ? r.data : null)).catch(() => setLastAttempt(null));
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!apiKey.trim()) { toast.error("Paste your AiSensy API key first."); return; }
    setSaving(true);
    try {
      await api.post("/aisensy/connect", { api_key: apiKey.trim(), label: "Primary" });
      toast.success("AiSensy connected.");
      setApiKey("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connect failed.");
    } finally { setSaving(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect AiSensy? This archives all your active campaigns.")) return;
    try {
      await api.delete("/aisensy/connection");
      toast.info("Disconnected. Campaigns archived.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed.");
    }
  };

  const copyWebhook = () => {
    const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const url = base + conn.webhook_url;
    navigator.clipboard.writeText(url);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  const copySecret = () => {
    navigator.clipboard.writeText(conn.webhook_secret || "");
    setCopiedSecret(true); setTimeout(() => setCopiedSecret(false), 2000);
  };

  const saveSecret = async () => {
    setSavingSecret(true);
    try {
      const r = await api.put("/aisensy/webhook-secret", { webhook_secret: secretDraft.trim() });
      toast.success(r.data?.signed_mode ? "Signed mode ON — webhooks now HMAC-verified." : "Secret cleared — falling back to token-only auth.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setSavingSecret(false); }
  };

  const installBridge = async () => {
    setInstalling(true);
    try {
      const r = await api.get("/employz-app/install-url");
      window.open(r.data.url, "_blank", "noopener");
      toast.info("Complete the install in the new tab, then refresh this panel.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start install.");
    } finally { setInstalling(false); }
  };

  const disconnectBridge = async () => {
    if (!window.confirm("Disconnect the GHL conversation bridge? Inbound WhatsApp messages will go back to note+task mirroring.")) return;
    try {
      await api.delete("/employz-app/install");
      toast.success("Bridge disconnected.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed.");
    }
  };

  if (!conn) {
    return <div className="text-sm text-zinc-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading AiSensy status…</div>;
  }

  if (conn.status !== "connected") {
    return (
      <div className="rounded-2xl border border-amber-500/20 bg-amber-500/5 p-6" data-testid="aisensy-user-panel">
        <div className="flex items-start gap-3">
          <Link2 className="w-5 h-5 text-amber-300 mt-0.5" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.24em] text-amber-300">// AiSensy</div>
            <h3 className="font-display font-black text-xl text-white tracking-tight mt-1">Connect your AiSensy account</h3>
            <p className="text-xs text-zinc-400 mt-1">Paste the API key from AiSensy → Manage → API Key. Nothing sends until you register a campaign and pass its verification test.</p>
            <div className="mt-3 flex gap-2">
              <input
                type="password"
                data-testid="aisensy-user-api-key"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Paste AiSensy API key (JWT)"
                className="flex-1 bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-white"
              />
              <button
                onClick={save}
                disabled={saving}
                data-testid="aisensy-user-connect-btn"
                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-4 py-2 border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 rounded-md disabled:opacity-40"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
                Connect
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-6" data-testid="aisensy-user-panel">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.24em] text-emerald-300">// AiSensy · connected</div>
          <div className="mt-1 text-sm text-white">
            {conn.label} · <span className="font-mono text-zinc-500">{conn.api_key_masked}</span>
          </div>
          <div className="text-[11px] text-zinc-500 mt-1">Connected {new Date(conn.connected_at).toLocaleString()}</div>
        </div>
        <button onClick={disconnect} data-testid="aisensy-user-disconnect" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-1 border border-zinc-800 text-zinc-400 rounded hover:border-rose-500/40 hover:text-rose-300"><Trash2 className="w-3 h-3" /> Disconnect</button>
      </div>
      <div className="mt-4 pt-4 border-t border-emerald-500/20">
        <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 mb-1">One-time setup: point AiSensy webhooks here</div>
        <p className="text-xs text-zinc-400 mb-2">In AiSensy → Manage → Live Chat → Settings → Webhook URL, paste this address so every inbound reply lands in your Replies tab:</p>
        <div className="flex items-center gap-2">
          <input readOnly value={(process.env.REACT_APP_BACKEND_URL || window.location.origin) + (conn.webhook_url || "")} className="flex-1 bg-zinc-950 border border-zinc-800 rounded-md px-2 py-1.5 text-[11px] text-zinc-400 font-mono" data-testid="aisensy-webhook-url" />
          <button onClick={copyWebhook} data-testid="aisensy-copy-webhook" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-1.5 border border-emerald-500/40 text-emerald-300 rounded">
            {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
          </button>
        </div>
        {conn.webhook_secret ? (
          <div className="mt-4 pt-4 border-t border-emerald-500/20">
            <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 mb-1 inline-flex items-center gap-1.5">
              <Shield className="w-3 h-3" /> Signed mode · HMAC verified
            </div>
            <p className="text-xs text-zinc-400 mb-2">Shared secret is on file. Every incoming AiSensy webhook is HMAC-SHA256 verified before we process it.</p>
            <div className="flex items-center gap-2">
              <input value={secretDraft} onChange={(e) => setSecretDraft(e.target.value)} className="flex-1 bg-zinc-950 border border-zinc-800 rounded-md px-2 py-1.5 text-[11px] text-zinc-300 font-mono" data-testid="aisensy-webhook-secret" />
              <button onClick={copySecret} data-testid="aisensy-copy-secret" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-1.5 border border-zinc-800 text-zinc-400 rounded">
                {copiedSecret ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
              </button>
              <button onClick={saveSecret} disabled={savingSecret || secretDraft === conn.webhook_secret} data-testid="aisensy-save-secret" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-1.5 border border-emerald-500/40 text-emerald-300 rounded disabled:opacity-40">
                {savingSecret ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-4 pt-4 border-t border-amber-500/20">
            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300 mb-1 inline-flex items-center gap-1.5">
              <ShieldOff className="w-3 h-3" /> Token-only mode · No HMAC
            </div>
            <p className="text-xs text-zinc-400 mb-2">
              Paste the <strong className="text-zinc-300">Shared Secret</strong> from AiSensy → <em>Project Settings → Webhooks → your subscription</em>. Once saved, every event will be HMAC-verified. Until then we accept events using only the URL token — customer replies will still flow, just less secure.
            </p>
            <div className="flex items-center gap-2">
              <input value={secretDraft} onChange={(e) => setSecretDraft(e.target.value)} placeholder="Paste AiSensy shared secret here…" className="flex-1 bg-zinc-950 border border-amber-500/30 rounded-md px-2 py-1.5 text-[11px] text-zinc-300 font-mono placeholder:text-zinc-700" data-testid="aisensy-webhook-secret" />
              <button onClick={saveSecret} disabled={savingSecret || !secretDraft.trim()} data-testid="aisensy-save-secret" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-1.5 border border-emerald-500/40 text-emerald-300 rounded disabled:opacity-40">
                {savingSecret ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
              </button>
            </div>
          </div>
        )}

        {/* GHL Conversation Bridge (per GHL_BRIDGE_INBOUND_BUILD B6) */}
        <div className="mt-4 pt-4 border-t border-zinc-800" data-testid="ghl-bridge-panel">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 mb-1 inline-flex items-center gap-1.5">
                <ExternalLink className="w-3 h-3" /> GHL Conversation Bridge
              </div>
              <div className="flex items-center gap-2 mb-1.5">
                {(() => {
                  const bs = bridge?.bridge_state || (bridge?.status === "active" ? "active" : bridge?.status === "reauth_needed" ? "reauth_needed" : "not_installed");
                  const badge = BRIDGE_BADGE[bs] || BRIDGE_BADGE.not_installed;
                  return (
                    <span data-testid={`bridge-badge-${bs}`} className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border ${badge.cls}`}>
                      Bridge: {badge.label}
                    </span>
                  );
                })()}
                {bridge?.location_id && (
                  <span className="text-[10px] text-zinc-500 font-mono truncate">loc {bridge.location_id}</span>
                )}
              </div>
              <div className="text-xs text-zinc-400" data-testid="bridge-reason">
                {bridge?.is_operational ? (
                  <span>Your WhatsApp conversations post natively inside GHL Conversations. Note+task mirroring is OFF while operational.</span>
                ) : (
                  <span>{bridge?.reason || "Inbound WhatsApp is mirrored to GHL via notes + tasks. Install the bridge to see conversations natively in GHL."}</span>
                )}
              </div>
              {lastAttempt && lastAttempt.outcome === "failure" && (
                <div className="mt-2 text-[10px] text-zinc-500" data-testid="bridge-last-attempt">
                  <span className="uppercase tracking-[0.16em]">Last install attempt:</span>{" "}
                  <span className="text-rose-400">{lastAttempt.outcome}</span>{" "}
                  <span className="text-zinc-600">· {lastAttempt.phase}</span>{" "}
                  {lastAttempt.attempted_at && (
                    <span className="text-zinc-600">· {new Date(lastAttempt.attempted_at).toLocaleString()}</span>
                  )}
                  {lastAttempt.ghl_status_code && (
                    <span className="text-zinc-600"> · GHL {lastAttempt.ghl_status_code}</span>
                  )}
                  {(lastAttempt.error || lastAttempt.ghl_error_body) && (
                    <pre className="mt-1 whitespace-pre-wrap text-[10px] text-rose-300/90 bg-zinc-950 border border-zinc-800 rounded p-2 font-mono max-h-32 overflow-auto">
                      {lastAttempt.ghl_error_body || lastAttempt.error}
                    </pre>
                  )}
                </div>
              )}
              {bridge?.last_inbound_bridge_diag && (
                <details className="mt-2 text-[10px] text-zinc-500" data-testid="bridge-last-inbound">
                  <summary className="cursor-pointer hover:text-zinc-300">
                    <span className="uppercase tracking-[0.16em]">Last inbound bridge post:</span>{" "}
                    {bridge.last_inbound_bridge_diag.error ? (
                      <span className="text-rose-400">failed</span>
                    ) : (
                      <span className="text-emerald-400">ok</span>
                    )}
                    {bridge.last_inbound_bridge_diag.status && (
                      <span className="text-zinc-600"> · HTTP {bridge.last_inbound_bridge_diag.status}</span>
                    )}
                    {bridge.last_inbound_bridge_at && (
                      <span className="text-zinc-600"> · {new Date(bridge.last_inbound_bridge_at).toLocaleString()}</span>
                    )}
                  </summary>
                  <div className="mt-1 space-y-1">
                    {bridge.last_inbound_bridge_diag.url && (
                      <div className="text-zinc-600 break-all">POST {bridge.last_inbound_bridge_diag.url}</div>
                    )}
                    {bridge.last_inbound_bridge_diag.request && (
                      <pre className="whitespace-pre-wrap bg-zinc-950 border border-zinc-800 rounded p-2 font-mono max-h-32 overflow-auto text-zinc-400">
{JSON.stringify(bridge.last_inbound_bridge_diag.request, null, 2)}
                      </pre>
                    )}
                    {(bridge.last_inbound_bridge_diag.response_body || bridge.last_inbound_bridge_diag.error) && (
                      <pre className={`whitespace-pre-wrap bg-zinc-950 border border-zinc-800 rounded p-2 font-mono max-h-32 overflow-auto ${bridge.last_inbound_bridge_diag.error ? "text-rose-300/90" : "text-emerald-300/90"}`}>
                        {bridge.last_inbound_bridge_diag.error || bridge.last_inbound_bridge_diag.response_body}
                      </pre>
                    )}
                  </div>
                </details>
              )}
              {!bridge?.provider_configured && bridge?.bridge_state !== "not_installed" && (
                <div className="mt-2 text-[10px] text-amber-400 inline-flex items-center gap-1" data-testid="bridge-provider-warning">
                  <AlertTriangle className="w-3 h-3" /> Server admin: <code>GHL_CONV_PROVIDER_ID</code> is not set — bridge cannot post to Conversations.
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {bridge?.is_operational ? (
                <button onClick={disconnectBridge} data-testid="bridge-disconnect" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2.5 py-1.5 border border-zinc-800 text-zinc-500 rounded">
                  <Trash2 className="w-3 h-3" /> Disconnect
                </button>
              ) : (
                <button onClick={installBridge} disabled={installing} data-testid="bridge-install" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2.5 py-1.5 border border-emerald-500/40 text-emerald-300 rounded">
                  {installing ? <Loader2 className="w-3 h-3 animate-spin" /> : <ExternalLink className="w-3 h-3" />} {bridge?.bridge_state === "not_installed" ? "Install bridge" : "Reinstall bridge"}
                </button>
              )}
            </div>
          </div>
          {bridge?.is_operational && bridge?.expires_at && (
            <div className="mt-2 text-[10px] text-zinc-600">
              Location <span className="font-mono">{bridge.location_id}</span> · token refreshes ~{new Date(bridge.expires_at).toLocaleString()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
